import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const SHOWCASE_ITEMS = [
  { key: 'customers', image: '/promo/customers.png' },
  { key: 'system', image: '/promo/system-mgmt.png' },
  { key: 'activate', image: '/promo/activation.png' },
];

export default function ShowcaseScroll() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);

  const items = useMemo(
    () =>
      SHOWCASE_ITEMS.map((item) => ({
        ...item,
        title: t.showcase[item.key].title,
        subtitle: t.showcase[item.key].subtitle,
      })),
    [t]
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setActive(0);
      setProgress(0);
      return undefined;
    }

    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = section.getBoundingClientRect();
      const total = section.offsetHeight - window.innerHeight;
      if (total <= 0) {
        setActive(0);
        setProgress(0);
        return;
      }

      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const nextProgress = scrolled / total;
      // Split the pinned scroll into equal steps (1 → 2 → 3), then release.
      const stepSize = 1 / items.length;
      const nextActive = Math.min(
        items.length - 1,
        Math.floor(nextProgress / stepSize)
      );

      setProgress(nextProgress);
      setActive(nextActive);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [items.length]);

  const activeItem = items[active] || items[0];

  return (
    <section
      ref={sectionRef}
      className="showcase-pin"
      aria-label={activeItem.title}
      style={{ '--showcase-progress': progress }}
    >
      <div className="showcase-pin-sticky">
        <div className="container showcase-pin-layout">
          <div className="showcase-pin-copy">
            <span className="showcase-step-num">
              {String(active + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
            </span>
            <div className="showcase-pin-text-wrap">
              {items.map((item, index) => (
                <div
                  key={item.key}
                  className={`showcase-pin-text${active === index ? ' is-active' : ''}`}
                >
                  <h2>{item.title}</h2>
                  <p>{item.subtitle}</p>
                </div>
              ))}
            </div>

            <div className="showcase-pin-bars" aria-hidden="true">
              {items.map((item, index) => (
                <span
                  key={item.key}
                  className={`showcase-pin-bar${active === index ? ' is-active' : ''}${index < active ? ' is-done' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="showcase-pin-visual">
            <div className="showcase-stage">
              {items.map((item, index) => (
                <img
                  key={item.key}
                  src={item.image}
                  alt={active === index ? item.title : ''}
                  className={`showcase-stage-img${active === index ? ' is-active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
